import React, { useCallback, useEffect, useState, useRef, TouchEvent, ReactNode } from 'react';
import { clamp } from 'lodash';

import classNames from 'classnames';
import { useMaxWidthTabletLarge } from '../../hooks/useMediaQueries';

import styles from './TooltipProvider.module.scss';

interface Props {
  renderTooltip: (xPercentage: number) => ReactNode;
  onShowTooltip?: (xPercentage: number) => void;
  onHideTooltip?: () => void;
  onScrub?: () => void;
  children: JSX.Element;
  isWindGraph?: boolean;
  showMobileHourlyWindGraph?: boolean;
  isSwellGraph?: boolean;
  isBarGraph?: boolean;
  isSurfGraph?: boolean;
  isHourly?: boolean;
  currentMarkerXPercentage?: number;
  columns?: number;
  parentRef?: HTMLDivElement;
  cssModules?: {
    tooltip?: string;
    root?: string;
  };
  forceShow?: boolean;
}

interface Style {
  top: number;
  left: number;
  right: number;
  bottom: number;
  transform: string;
}

interface Tooltip {
  style: Style;
  xPercentage: number;
}

const OFFSET_FROM_CURSOR = 8;

const TooltipProvider: React.FC<Props> = ({
  currentMarkerXPercentage = 0,
  columns = 0,
  onShowTooltip = null,
  onHideTooltip = null,
  onScrub = null,
  isHourly = false,
  isWindGraph = false,
  showMobileHourlyWindGraph = false,
  isSwellGraph = false,
  isBarGraph = false,
  isSurfGraph = false,
  parentRef = null,
  children,
  renderTooltip,
  cssModules = undefined,
  forceShow = false,
}) => {
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isMobile = useMaxWidthTabletLarge();
  const isUpdatedGraph = isWindGraph || isSwellGraph || isSurfGraph;
  const showMobileTooltip = isMobile && isUpdatedGraph;

  const hideTooltip = useCallback(() => {
    if (!forceShow) {
      if (onHideTooltip) onHideTooltip();
      setTooltip(null);
    }
  }, [forceShow, onHideTooltip]);

  const showTooltip = useCallback(
    (clientX: number, clientY: number, touch = false, markerXPercentage?: number) => {
      if (!boxRef.current) {
        return;
      }
      const box = boxRef.current?.getBoundingClientRect();
      const currentMarkerExists = markerXPercentage !== undefined;
      let xFromLeft = currentMarkerExists ? box.width * markerXPercentage : clientX - box.left;
      /**
       * On the mobile bar graph tooltip we want to center the tooltip in the selected column
       * To do this we can figure out which column is selected using the `xFromLeft` value
       * and then adjust the `xFromLeft` to be that new centered value
       */
      if (isBarGraph) {
        const columnSize = box.width / columns;
        const currentColumn = Math.floor(xFromLeft / columnSize) + 1;
        xFromLeft = currentColumn * columnSize - columnSize / 2;
      }

      const yFromTop = clientY - box.top;

      if (
        !showMobileTooltip &&
        (yFromTop <= 0 || xFromLeft > box.width || yFromTop > box.height) &&
        !forceShow
      ) {
        hideTooltip();
        return;
      }

      const xPercentage = currentMarkerExists ? markerXPercentage : xFromLeft / box.width;
      if (onShowTooltip) onShowTooltip(xPercentage);

      const style = {} as Style;

      if (touch || showMobileTooltip) {
        style.top = 0;

        if (isWindGraph) {
          style.top = showMobileHourlyWindGraph ? -48 : -64;
          style.left = xFromLeft + OFFSET_FROM_CURSOR;
        } else if (isSwellGraph) {
          style.top = -100;
          style.left = 0;
        } else if (isSurfGraph) {
          style.top = -62;
          style.left = xFromLeft;
        } else if (xPercentage < 0.5) {
          style.right = 0;
        } else {
          style.left = 0;
        }
      } else {
        style.top = yFromTop;
        style.left = xFromLeft + OFFSET_FROM_CURSOR;
      }

      const newTooltip = {
        style,
        // We clamp the xPercentage to be between 0 and 1 because there's no way the hover can be
        // outside the box. When we use `forceShow` there is a chance that the hover is slightly to
        // the left of the box and we don't short circuit the logic, so we end up with a negative
        // xPercentage. Here we avoid that.
        xPercentage: clamp(xPercentage, 0, 1),
      };

      setTooltip(newTooltip);

      if (!tooltipRef?.current) {
        return;
      }
      if (touch || showMobileTooltip) {
        if (isWindGraph || isSurfGraph) {
          const tooltipWidth = tooltipRef.current.getBoundingClientRect().width;
          let tooltipLeft = style.left;

          // This is used to center the tooltip above the selected column
          tooltipLeft -= tooltipWidth / 2;

          // Account for the tooltip going off screen
          const tooltipRightEdge = tooltipLeft + tooltipWidth;
          const isOutsideBox = tooltipRightEdge > box.width;
          if (isOutsideBox) {
            tooltipLeft -= tooltipRightEdge - box.width;
          } else if (tooltipLeft < 0) {
            tooltipLeft = 0;
          }
          tooltipRef.current.style.left = `${tooltipLeft}px`;
          tooltipRef.current.style.transform = '';
        } else {
          tooltipRef.current.style.transform = '';
        }
      } else {
        const minX = tooltipRef.current.offsetWidth + 30;

        const tooltipLeftEdge = clientX - tooltipRef.current.offsetWidth;
        const shouldStayWithinParent =
          !!parentRef && tooltipLeftEdge < parentRef?.getBoundingClientRect()?.left;
        if (clientX <= minX) {
          tooltipRef.current.style.transform = 'translateY(-100%)';
        } else if (shouldStayWithinParent) {
          // If we pass a parent ref, then it means we want the tooltip to stay within the X axis
          // bounds of the parent. So above we have calculated the `tooltipLeftEdge` and comapre it
          // to the parent left edge
          tooltipRef.current.style.transform = 'translateY(-100%)';
        } else {
          tooltipRef.current.style.transform = `translate(-${
            tooltipRef.current.offsetWidth + 2 * OFFSET_FROM_CURSOR
          }px, -100%)`;
        }
      }
    },
    [
      columns,
      forceShow,
      hideTooltip,
      isBarGraph,
      isSurfGraph,
      isSwellGraph,
      isWindGraph,
      onShowTooltip,
      parentRef,
      showMobileHourlyWindGraph,
      showMobileTooltip,
    ],
  );

  const handleMouseMove = ({ clientX, clientY }: { clientX: number; clientY: number }) => {
    showTooltip(clientX, clientY);
    if (onScrub) onScrub();
  };

  const handleTouch = (e: TouchEvent<HTMLDivElement>) => {
    const { clientX, clientY } = e.changedTouches[0];
    showTooltip(clientX, clientY, true);
    if (onScrub) onScrub();
  };

  useEffect(() => {
    if (isMobile && (isWindGraph || isSwellGraph || isSurfGraph)) {
      showTooltip(0, 0, true, currentMarkerXPercentage);
    }
  }, [currentMarkerXPercentage, isMobile, isSurfGraph, isSwellGraph, isWindGraph, showTooltip]);

  useEffect(() => {
    if (isHourly) {
      hideTooltip();
    }
  }, [isHourly, hideTooltip]);

  useEffect(() => {
    if (forceShow) {
      showTooltip(0, 0, true, currentMarkerXPercentage);
    }
  }, [currentMarkerXPercentage, forceShow, showTooltip]);

  return (
    <div
      ref={boxRef}
      className={classNames(styles.tooltipProvider, cssModules?.root)}
      onMouseMove={handleMouseMove}
      onMouseLeave={showMobileTooltip ? undefined : hideTooltip}
      onTouchStart={handleTouch}
      onTouchMove={handleTouch}
      onTouchEnd={showMobileTooltip ? undefined : hideTooltip}
    >
      {children}
      <div
        className={classNames(
          {
            [styles.tooltipProviderTooltip]: true,
            [styles.tooltipProviderTooltipHide]: !tooltip,
          },
          cssModules?.tooltip,
        )}
        ref={tooltipRef}
        style={tooltip ? tooltip.style : {}}
      >
        {tooltip ? renderTooltip(tooltip.xPercentage) : null}
      </div>
    </div>
  );
};

export default TooltipProvider;
